import React from 'react';
import { Grid, Paper, Typography, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ClaimsStats } from './ClaimDetailsTypes';

const useStyles = makeStyles((theme) => ({
  statCard: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1a1f36',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
  },
  title: {
    fontSize: 16,
    fontWeight: 500,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.8)',
    // MUI Typography resets margins; explicit zero keeps layout stable across variants
    margin: 0,
  },
  amountValue: {
    fontSize: 32,
    fontWeight: 500,
    color: '#4caf50',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    width: '100%',
  },
  totalNumber: {
    fontSize: 48,
    fontWeight: 500,
    textAlign: 'center',
    margin: theme.spacing(2, 0),
  },
}));

type ClaimsFinancialKPICardsProps = {
  stats: ClaimsStats | null;
  loading: boolean;
};

/**
 * Formats a dollar amount as "$1,234".
 * Falls back to "$0" for non-finite inputs (NaN, Infinity) so the UI
 * never renders "$NaN" if the API returns a bad value.
 */
const formatAmount = (value: number): string => {
  if (!isFinite(value)) return '$0';
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

type CardDef = {
  key: string;
  label: string;
  value: number;
};

/**
 * Renders four financial KPI cards as Grid items.
 * Must be placed inside a <Grid container> in the parent.
 *
 * Accessibility:
 *  - Each card is a <section> with aria-label so screen readers can navigate
 *    between named regions.
 *  - aria-busy signals that content is still loading.
 *  - The value Typography carries aria-live="polite" so readers announce the
 *    updated figure without interrupting the user.
 *  - The loading placeholder carries role="status" and aria-label so it is
 *    announced as "Loading" rather than the literal "..." characters.
 *  - The title uses component="h3" for semantic heading structure.
 */
export const ClaimsFinancialKPICards: React.FC<ClaimsFinancialKPICardsProps> = React.memo(
  ({ stats, loading }) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const cards: CardDef[] = [
      {
        key: 'totalBillAmount',
        label: t('Total Bill Amount'),
        value: stats?.totalBilledAmount ?? 0,
      },
      {
        key: 'totalApprovedBillAmount',
        label: t('Total Approved Bill Amount'),
        value: stats?.totalApprovedAmount ?? 0,
      },
      {
        key: 'totalAllowedAmount',
        label: t('Total Allowed Amount'),
        value: stats?.totalAllowedAmount ?? 0,
      },
      {
        key: 'totalOpenBillAmount',
        label: t('Total Open Bill Amount (No Verdict Yet)'),
        // totalOpenBillAmount is optional in ClaimsStats; ?? 0 handles undefined
        value: stats?.totalOpenBillAmount ?? 0,
      },
    ];

    return (
      <>
        {cards.map(({ key, label, value }) => (
          <Grid item xs={12} md={6} lg={3} key={key}>
            {/* component="section" + aria-label creates a named <section> landmark */}
            <Paper
              className={classes.statCard}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              component={'section' as any}
              aria-label={label}
              aria-busy={loading}
            >
              {/* component="h3" gives the card title semantic heading structure */}
              <Typography
                className={classes.title}
                component={'h3' as any}
              >
                {label}
              </Typography>

              {loading ? (
                // role="status" + aria-label replaces "..." with a readable announcement
                <Typography
                  className={classes.totalNumber}
                  role="status"
                  aria-label={t('Loading')}
                >
                  ...
                </Typography>
              ) : (
                // aria-live announces the refreshed value after a filter change
                <Typography
                  className={classes.amountValue}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {formatAmount(value)}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </>
    );
  }
);

ClaimsFinancialKPICards.displayName = 'ClaimsFinancialKPICards';
